import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  type Asset,
  type Customer,
  type Policy,
  assetControllerEditAsset,
  customerControllerEditCustomer,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
  policyControllerEditPolicy,
  toKeyValueFields,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { type UpdateFieldQuickAction, useKorintProductConfig } from 'korint-sdk/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../forms/select/select';
import { Spinner } from '../../ui/spinner';
import type { EntityValue } from '../InformationCard/InformationCard.type';
import { InformationFields } from '../InformationCard/InformationFields';

type UpdateFieldQuickActionDialogProps = {
  action: UpdateFieldQuickAction;
  onClose: () => void;
};

type Target = {
  entity: Policy | Asset | Customer;
  updateEntity: (key: string, value: EntityValue) => Promise<void>;
};

export function UpdateFieldQuickActionDialog({
  action,
  onClose,
}: UpdateFieldQuickActionDialogProps) {
  const { t } = useTranslation();
  const { policy, branchIdForPolicy, mutate } = usePolicyOnDashboard();
  const branchParams = branchIdForPolicy ? { branchId: branchIdForPolicy } : undefined;
  const [selectedId, setSelectedId] = useState<string>();
  const { productsConfig } = useKorintProductConfig();
  const customerFieldsConfig = productsConfig[policy.productId]?.customerInformations?.fieldsConfig;

  const {
    data: assets,
    isLoading: areAssetsLoading,
    refetch: refetchAssets,
  } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
      ...(branchIdForPolicy ? { branchId: branchIdForPolicy } : {}),
    }),
    enabled: action.entity === 'asset',
    select: ({ data }) =>
      data.map(asset => ({
        ...asset,
        customFields: keyValueFieldsToObject(asset.customFields ?? []),
      })),
  });

  const customerQueries = useQueries({
    queries: (action.entity === 'customer' ? (policy.customers ?? []) : []).map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, branchParams),
    })),
  });
  const customers = customerQueries.flatMap(({ data }) => (data ? [data] : []));
  const areCustomersLoading = customerQueries.some(({ isLoading }) => isLoading);

  const choices =
    action.entity === 'asset'
      ? (assets ?? []).map(asset => ({ id: asset.id, label: asset.name ?? asset.id }))
      : action.entity === 'customer'
        ? customers.map(customer => ({
            id: customer.id,
            label: customerDisplayName([customer], customerFieldsConfig) ?? customer.id,
          }))
        : [];

  const activeId = selectedId ?? choices[0]?.id;

  function buildTarget(): Target | undefined {
    if (action.entity === 'policy') {
      return {
        entity: policy,
        updateEntity: async (key, value) => {
          await policyControllerEditPolicy(
            policy.id,
            { customFields: toKeyValueFields({ [key]: value }) },
            branchParams,
          );
          await mutate();
        },
      };
    }

    if (action.entity === 'asset') {
      const asset = (assets ?? []).find(({ id }) => id === activeId);
      if (!asset) return undefined;

      return {
        entity: asset,
        updateEntity: async (key, value) => {
          await assetControllerEditAsset(
            asset.id,
            { customFields: toKeyValueFields({ [key]: value }) },
            branchParams,
          );
          await refetchAssets();
        },
      };
    }

    const customer = customers.find(({ id }) => id === activeId);
    if (!customer) return undefined;

    return {
      entity: { ...customer, customFields: keyValueFieldsToObject(customer.customFields ?? []) },
      updateEntity: async (key, value) => {
        await customerControllerEditCustomer(
          customer.id,
          { customFields: toKeyValueFields({ [key]: value }) },
          branchParams,
        );
        await customerQueries.find(({ data }) => data?.id === customer.id)?.refetch();
      },
    };
  }

  const target = buildTarget();
  const isLoading = areAssetsLoading || areCustomersLoading;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`policyQuickActions.${action.id}.label`)}</DialogTitle>
        </DialogHeader>
        {choices.length > 1 && (
          <Select value={activeId} onValueChange={setSelectedId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {choices.map(({ id, label }) => (
                <SelectItem key={id} value={id}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isLoading && <Spinner />}
        {!isLoading && !target && <p>{t('policyQuickActions.noTarget')}</p>}
        {target && (
          <InformationFields
            productId={policy.productId}
            entityType={action.entity}
            entity={target.entity}
            fieldsConfig={[...action.fields]}
            updateEntity={target.updateEntity}
            forceReadonly={policy.status === 'POLICY_STOPPED'}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
